import type { Tutorial } from '../types'

const IMG = '/images/tutorials/web/wellness-goals'

export const wellnessGoalsWebTutorial: Tutorial = {
  slug: 'wellness-goals',
  platform: 'web',
  title: 'Wellness Goals',
  subtitle: 'Create, track, and check in from the dashboard',
  date: '2026-07-09',
  category: 'Core Features',
  tags: ['Wellness Goals', 'check-in', 'streak', 'dashboard'],
  description:
    'The web goals dashboard shows every active goal with filters, insights, and one-click access to quick check-in — the same loop as mobile, with more room for analytics.',
  highlights: [
    'Goals list with search, category filters, and inline create.',
    'Set a goal by talking it through with Coach: the create button opens a ready-made request.',
    'Quick check-in page at /wellness-goals/:id/checkin — no LLM, same API as mobile.',
  ],
  coverImage: `${IMG}/01-goals-list.png`,
  sections: [
    {
      heading: 'The goals dashboard',
      paragraphs: [
        '**Wellness Goals** in the sidebar opens your full goals list — status, category, last check-in, and shortcuts to open or check in on each goal.',
      ],
      image: {
        src: `${IMG}/01-goals-list.png`,
        alt: 'Wellness Goals list on Livewello web',
        caption: 'Goals list',
      },
    },
    {
      heading: 'Creating a goal',
      paragraphs: [
        'Tap **Create goal** to open the dedicated creation page — title, description, target date, and category. Goals can also be spawned from a Wellness Coach suggested-goal card.',
      ],
      image: {
        src: `${IMG}/02-create-goal.png`,
        alt: 'Create wellness goal form on web',
        caption: 'Create goal',
      },
    },
    {
      heading: 'Goal detail and quick check-in',
      paragraphs: [
        'Each goal has its own detail page with streak visualization and analytics. **Check in** opens a focused quick-check-in page — Yes / Partly / No plus an optional note, calling `POST /goal-checkin/quick` directly.',
      ],
      image: {
        src: `${IMG}/03-goal-detail.png`,
        alt: 'Wellness goal detail page with streak on web',
        caption: 'Goal detail',
      },
    },
    {
      heading: 'After check-in',
      paragraphs: [
        'The check-in posts immediately and the streak updates. Same-day debounce prevents accidental double-logging.',
      ],
      image: {
        src: `${IMG}/04-quick-checkin.png`,
        alt: 'Quick check-in page on web',
        caption: 'Quick check-in',
      },
    },
  ],
  whyMatters:
    'The web dashboard is where most users manage several goals at once — filters, insights panels, and accountability partner banners all live here. Quick check-in stays fast so daily logging still takes seconds.',
  nextSlug: 'health-records',
  relatedSlugs: ['wellness-coach', 'accountability-partner-workflow'],
  meta: {
    description: 'Wellness Goals on Livewello web: dashboard, creation, and quick check-in walkthrough.',
    ogImage: `${IMG}/01-goals-list.png`,
  },
}
